/*
 * @Author: liyifan
 * @Date: 2026-01-05 16:36:07
 * @Last Modified by: liyifan
 * @Last Modified time: 2026-01-21 17:49:12
 * @description: 产品对比弹窗
 */

import { getCompareDetail } from "@utils/api.js";

if (!customElements.get("v5-app-compare-modal")) {
  class V5AppCompareModal extends HTMLElement {
    /**
     * 监听组件属性变化
     * @returns {string[]} 需要监听的属性键
     */
    static get observedAttributes() {
      // 监听属性变化，用于外部配置与联动
      return ["variant-ids", "page-num"];
    }
    /**
     * 构造函数，初始化基础状态与文本
     */
    constructor() {
      super();
      // 初始化基础状态
      this._inited = false;
      this._variantIds = [];
      this._pageNum = null;
      this._showDiff = false;
      this.isMobile = window.innerWidth < 1024;
      this.text = {};
    }
    /**
     * 生命周期：插入到文档时执行
     * 完成一次性渲染、初始化与事件绑定
     */
    connectedCallback() {
      if (this._inited) return;
      this._inited = true;

      // 读取一次性配置（非动态）
      this._showDiff = this.getAttribute("show-diff") === "true";
      this.openAutoDiscountPrice = this.getAttribute("data-open-auto-discount-price") === "true";
      this._pageNum = this.getAttribute("page-num");
      this.text.shop_now = window.pageInfo?.strings?.product?.shopNow;
      this.text.show_diff = window.pageInfo?.strings?.product?.showDiffText;

      // 首次渲染与绑定
      this._renderOnce();
      this._syncVariantIds();
      this._bindExternalOpen();                    
      this._bindGlobalClick();

      this.bindEvent()
    }
    /**
     * 属性变更回调
     * @param {string} name 属性名
     * @param {string|null} oldValue 旧值
     * @param {string|null} newValue 新值
     */
    attributeChangedCallback(name, oldValue, newValue) {
      if (oldValue === newValue) return;
      // 支持通过属性驱动组件行为
      if (name === "variant-ids") {
        this._syncVariantIds(newValue);
      } else if (name === "page-num") {
        this._pageNum = newValue;
      }
    }
    /**
     * 绑定组件内部互动事件（切换差异、手风琴）
     */
    bindEvent() {
        // 切换“只看不同”
        this._modalEl.addEventListener('click', (e) => {
            const toggleWrapper = e.target.closest(".toggle-diff-wrapper");
            if (toggleWrapper) {
                this._showDiff = !this._showDiff;
                this._updateDetails();
            }
        })

        // 对比详情的手风琴切换事件
        this._compareDetailsEl.addEventListener("click", (e) => {
            const header = e.target.closest(".compare-header");
            if (header) {
                const section = header.closest(".compare-section");
                if (section) {
                    const content = section.querySelector(".compare-content");
                    if (header.classList.contains("active")) {
                    header.classList.remove("active");
                    content.style.maxHeight = "0px";
                    } else {
                    header.classList.add("active");
                    content.style.maxHeight = content.scrollHeight + "px";
                    }
                }
            }
        });
    }
    /**
     * 绑定页面级点击打开事件
     * 支持通过 data-open-v5-compare-modal 属性触发
     */
    _bindGlobalClick() {
      if (window.__v5CompareModalClickBound) return;
      window.__v5CompareModalClickBound = true;
      // 页面内任意按钮通过 data-open-v5-compare-modal 触发打开
      document.addEventListener("click", (e) => {
        const t = e.target.closest("[data-open-v5-compare-modal]");
        if (!t) return;
        const sel = t.getAttribute("data-open-v5-compare-modal");
        const rawIds = t.getAttribute("data-variant-ids");
        const pageNum = t.getAttribute("data-page-num");
        let el = null;
        if (sel) {
          el = document.getElementById(sel) || document.querySelector(sel);
        } else {
          el = document.querySelector("v5-app-compare-modal");
        }
        const ids = this._parseVariantIds(rawIds);
        if (el && typeof el.open === "function") el.open(ids, pageNum);
      });
    }
    /**
     * 设置对比的 variantIds 并刷新内容
     * @param {string[]|string} ids 数组、JSON 字符串或逗号分隔字符串
     */
    setVariantIds(ids) {
      // 统一解析传入的 ID 格式并刷新内容
      this._variantIds = this._parseVariantIds(ids);
      this._updateDetails();
    }

    /**
     * 手动刷新内容
     */
    refresh() {
      this._updateDetails();
    }

    /**
     * 从属性同步初始对比 ID
     */
    _syncVariantIds() {
      // 从属性同步初始对比 ID
      const value = this.getAttribute("variant-ids");
      if (!value) return;

      try {
        this._variantIds = JSON.parse(value);
      } catch {
        this._variantIds = [];
      }
      this._updateDetails();
    }
    /**
     * 初次渲染弹窗结构与骨架，并缓存常用节点
     */
    _renderOnce() {
      this.innerHTML = `
        <div class="v5-app-compare-modal__mask fixed inset-0 z-[99999] bg-[#4D4D4D66] opacity-0 pointer-events-none transition-opacity duration-300 ease-out"></div>
        <div class="v5-app-compare-modal fixed inset-x-0 mx-auto lg:w-[68.5rem] w-full bottom-0 z-[99999] bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.12)] min-h-[60dvh] max-h-[90dvh] flex flex-col transform translate-y-full transition-transform duration-300 ease-out">
            <div class="v5-app-compare-modal__header flex justify-end items-center py-4 px-4 lg:px-[1.875rem]">
                <button class="v5-app-compare-modal__close-btn w-6 h-6 cursor-pointer" type="button">
                    <svg  xmlns="http://www.w3.org/2000/svg" fill="#242F20" viewBox="0 0 24 24" stroke-width="1.5" stroke="#242F20" aria-hidden="true" width="24" height="24">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                </button>
            </div>
            <div class="v5-app-compare-modal__body overflow-auto min-h-[50dvh]"> 
                <div class="px-4 lg:pr-[3.75rem] lg:pl-[11.5625rem] w-full mx-auto text-left grid grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem]  bg-[#fff] gap-4 lg:gap-6 sticky top-0 z-10 product-select">
                    <div class="animate-pulse">
                        <div class="w-full h-12 bg-[#F5F5F5] rounded mb-3"></div>
                    </div>
                    <div class="animate-pulse">
                        <div class="w-full h-12 bg-[#F5F5F5] rounded mb-3"></div>
                    </div>
                    <div class="hidden lg:block animate-pulse">
                        <div class="w-full h-12 bg-[#F5F5F5] rounded mb-3"></div>
                    </div>
                </div>
                <div class="px-4 lg:pr-[3.75rem] lg:pl-[11.5625rem] w-full mx-auto text-left grid grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem] bg-[#fff] gap-4 lg:gap-6 product-detail">
                    <div class="animate-pulse">
                        <div class="w-full h-[6.25rem] bg-[#F5F5F5] rounded mb-4"></div>
                        <div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div>
                        <div class="h-4 bg-[#F5F5F5] rounded w-1/2 mb-4"></div>
                        <div class="h-8 bg-[#F5F5F5] rounded w-1/3"></div>
                    </div>
                    <div class="animate-pulse">
                        <div class="w-full h-[6.25rem] bg-[#F5F5F5] rounded mb-4"></div>
                        <div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div>
                        <div class="h-4 bg-[#F5F5F5] rounded w-1/2 mb-4"></div>
                        <div class="h-8 bg-[#F5F5F5] rounded w-1/3"></div>
                    </div>
                    <div class="hidden lg:block animate-pulse">
                        <div class="w-full h-[6.25rem] bg-[#F5F5F5] rounded mb-4"></div>
                        <div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div>
                        <div class="h-4 bg-[#F5F5F5] rounded w-1/2 mb-4"></div>
                        <div class="h-8 bg-[#F5F5F5] rounded w-1/3"></div>
                    </div>
                </div>
                <div class="px-4 lg:pl-[7.8125rem] flex justify-center items-center py-4 toggle-btn">
                    <div class="col-span-2 lg:col-span-4 flex justify-center items-center bg-white animate-pulse">
                        <div class="flex items-center justify-center w-full">
                        <div class="w-11 h-6 bg-[#F5F5F5] rounded-full"></div>
                        <div class="ml-3 lg:ml-4 h-6 lg:h-7 bg-[#F5F5F5] rounded w-24 lg:w-48"></div>
                    </div>
                </div>
            </div>
            <div class="compare-details lg:px-[3.75rem] bg-[#fff]">
                <div class="compare-section animate-pulse">
                    <div class="compare-header bg-[#E8E8E5] h-[3.75rem] px-4 lg:px-6 flex items-center justify-between border-b border-solid border-[#E2E3E3]">
                        <div class="h-6 w-1/3 bg-[#dcdcdc] rounded"></div>
                    </div>
                    <div class="compare-content border-t border-[#E2E3E3] bg-white">
                        <div class="grid grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem] lg:pl-[7.8125rem] border-b border-[#E2E3E3]">
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] hidden lg:block"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        </div>
                        <div class="grid grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem] lg:pl-[7.8125rem]">
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] hidden lg:block"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        </div>
                    </div>
                </div>
                <div class="compare-section animate-pulse">
                    <div class="compare-header bg-[#E8E8E5] h-[3.75rem] px-4 lg:px-6 flex items-center justify-between border-b border-solid border-[#E2E3E3]">
                        <div class="h-6 w-1/3 bg-[#dcdcdc] rounded"></div>
                    </div>
                    <div class="compare-content border-t border-[#E2E3E3] bg-white">
                        <div class="grid grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem] lg:pl-[7.8125rem] border-b border-[#E2E3E3]">
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] hidden lg:block"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        </div>
                        <div class="grid grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem] lg:pl-[7.8125rem]">
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] border-r border-[#E2E3E3]"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                            <div class="p-6 min-h-[5rem] hidden lg:block"><div class="h-4 bg-[#F5F5F5] rounded w-3/4 mb-2"></div><div class="h-4 bg-[#F5F5F5] rounded w-1/2"></div></div>
                        </div>
                    </div>
                </div>
            </div>
            </div>
        </div>
    `;
      this._modalEl = this.querySelector(".v5-app-compare-modal");
      this._maskEl = this.querySelector(".v5-app-compare-modal__mask");
      this._modalBodyEl = this.querySelector('.v5-app-compare-modal__body');
      if (this._maskEl && !document.body.contains(this._maskEl)) {
        document.body.appendChild(this._maskEl);
      }
      if (this._modalEl && !document.body.contains(this._modalEl)) {
        document.body.appendChild(this._modalEl);
      }
      this._productSelectEl = this._modalEl.querySelector('.product-select');
      this._productDetailEl = this._modalEl.querySelector('.product-detail');
      this._toggleBtnEl = this._modalEl.querySelector('.toggle-btn');
      this._compareDetailsEl = this._modalEl.querySelector('.compare-details');
      const closeBtn = this._modalEl.querySelector(".v5-app-compare-modal__close-btn");

      closeBtn && closeBtn.addEventListener("click", () => this.close());
      this._maskEl && this._maskEl.addEventListener("click", () => this.close());
    }
    /**
     * 打开弹窗
     * @param {string[]|string} ids 可选，打开前更新对比 ID
     * @param {string|number} pageNum 可选，外部传入的 pageNum
     */
    open(ids, pageNum) {
      if (pageNum) this._pageNum = pageNum;
      if (ids) this.setVariantIds(ids);
      if (!this._modalEl) return;

      // 显示遮罩
      if (this._maskEl) {
        this._maskEl.classList.remove("opacity-0", "pointer-events-none");
        this._maskEl.classList.add("opacity-100", "pointer-events-auto");
      }

      this._modalEl.classList.add("translate-y-0");
      this._modalEl.classList.remove("translate-y-full");

      // 锁定页面滚动
      document.body.style.overflow = "hidden";
    }
    /**
     * 关闭弹窗并恢复页面滚动
     */
    close() {
      // 关闭弹窗并恢复滚动
      if (!this._modalEl) return;

      // 隐藏遮罩
      if (this._maskEl) {
        this._maskEl.classList.remove("opacity-100", "pointer-events-auto");
        this._maskEl.classList.add("opacity-0", "pointer-events-none");
      }

      this._modalEl.classList.add("translate-y-full");
      this._modalEl.classList.remove("translate-y-0");
      document.body.style.overflow = "";
    }

    /**
     * 绑定外部打开入口
     * 支持 4 种调用方式：
     *
     * 1. 全局函数调用 (JS推荐):
     *    window.openV5CompareModal(null, [ids...], pageNum); // 打开默认弹窗
     *    window.openV5CompareModal('#my-modal', [ids...], pageNum); // 指定弹窗
     *
     * 2. HTML 属性声明 (模板推荐):
     *    <button data-open-v5-compare-modal data-variant-ids="id1,id2" data-page-num="1">打开</button>
     *
     * 3. 实例方法调用:
     *    document.querySelector('v5-app-compare-modal').open([ids...], pageNum);
     *
     * 4. 自定义事件:
     *    document.dispatchEvent(new CustomEvent('v5-app-compare-modal:open', {
     *      detail: { variantIds: [ids...], pageNum: 1 }
     *    }));
     */
    _bindExternalOpen() {
      // 支持通过自定义事件或全局函数打开
      const id = this.id;
      document.addEventListener("v5-app-compare-modal:open", (e) => {
        const targetId = e?.detail?.id;
        const variantIds = e?.detail?.variantIds;
        const pageNum = e?.detail?.pageNum;
        if (!targetId || targetId === id) this.open(variantIds, pageNum);
      });
      if (!window.openV5CompareModal) {
        window.openV5CompareModal = (target, variantIds, pageNum) => {
          let el = null;
          if (!target) {
            el = this;
          } else if (typeof target === "string") {
            el = document.getElementById(target) || document.querySelector(target);
          } else if (target instanceof HTMLElement) {
            el = target;
          }
          if (el && typeof el.open === "function") el.open(variantIds, pageNum);
        };
      }
    }
    /**
     * 解析传入的 ID
     * @param {any} raw 原始入参
     * @returns {string[]} 解析后的 ID 数组
     */
    _parseVariantIds(raw) {
      // 解析传入的 ID 支持：数组、JSON 字符串、逗号分隔字符串
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        try {
          const j = JSON.parse(raw);
          if (Array.isArray(j)) return j;
        } catch {}
        return raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    }
    // 更新对比产品详情
    /**
     * 拉取最新对比详情并渲染到弹窗
     * - 渲染标题区、产品信息、只看不同开关、详情表格
     */
    async _updateDetails() {
      // 拉取数据并分别渲染头部、详情与开关
      if (!this._modalBodyEl) return;
      if (!this._variantIds || this._variantIds.length === 0) return;
      // 
      const params = {
        pageType: this._pageNum ? 4 : 3,
        pageNum: this._pageNum,
        platformType: this.isMobile ? 2 : 1,
        showDiff: this._showDiff,
        skuIdList: this._variantIds
      }
      const { data } = await getCompareDetail(params);
      if (!data.dictInfoResps || data.dictInfoResps.length === 0) {
        if (this._productSelectEl) this._productSelectEl.innerHTML = "";
        if (this._productDetailEl) this._productDetailEl.innerHTML = "";
        if (this._toggleBtnEl) this._toggleBtnEl.innerHTML = "";
        if (this._compareDetailsEl) {
          this._compareDetailsEl.innerHTML = `
            <div class="flex items-center justify-center py-8">
              <span class="text-sm text-[#242F20]">No Data</span>
            </div>
          `;
        }
        return;
      };
      // 渲染产品标题
      this._productSelectEl.innerHTML = this.renderProductSelect(data.fourProductResps || []);
      // 渲染产品信息
      this._productDetailEl.innerHTML = this.renderProductDetail(data.fourProductResps || []);
      // 渲染对比开关
      this._toggleBtnEl.innerHTML = this.renderToggleBtn();
      // 渲染对比详情
      this._compareDetailsEl.innerHTML = this.renderCompareDetails(data.dictInfoResps || []);
    }
    /**
     * 渲染产品标题
     * @param {*} list 对比产品列表
     * @returns HTML字符串
     */
    renderProductSelect(list = []){
        if (list.length === 0) return "";
        let html = "";
        list.forEach((item) => {
            const { title } = item
            html += `
              <div class="relative w-full h-full select-item">
                  <div class="flex items-center border bg-[#fff] border-[#E2E3E3] border-solid h-full p-2">
                      <span class="pointer-events-none font-semibold text-xs lg:text-base text-[#242F20]"> ${title} </span>
                  </div>
              </div>
            `
        })
        return html
    }

    /**
     * 渲染产品信息（图片、描述与价格、CTA）
     * @param {Array} list 对比产品列表
     * @returns {string} HTML 字符串
     */
    renderProductDetail(list = []){
        if (list.length === 0) return "";
        let html = "";
        list.forEach((item) => {
            const {
                skuId,
                goodUrl,
                image,
                variantDesc,
                autoDiscountPrice,
                shopifyFrontprice,
                shopifyFrontOriginalPrice
            } = item
            // 计算当前价格和原价
            const current_price = Math.min( autoDiscountPrice || 0, shopifyFrontprice || 0 );
            const origin_price = Math.max( autoDiscountPrice || 0, shopifyFrontprice || 0, shopifyFrontOriginalPrice || 0 );
            html += `
                <div class="lg:pr-6">
                    <div class="w-full my-2 lg:mt-3 lg:mb-4 h-[4.875rem] lg:h-[6.25rem] images">
                        <a href="${goodUrl}?variant=${skuId}">
                            <img class="h-[4.875rem] lg:h-[6.25rem] aspect-[1/1]" width="100" height="100" loading="lazy" src="${image}&width=100">
                        </a>
                    </div>
                    <div class="h-6 lg:h-[2.5rem] text-[0.625rem] leading-3 lg:text-sm font-normal text-[#242F20] mb-3 lg:mb-4 line-clamp-2 desc">${
                        variantDesc || ""
                    }</div>
                    <div class="text-[#242F20] price mb-2">
                        <span class="lg:text-xl text-sm font-semibold">
                            ${ this.openAutoDiscountPrice &&  item.autoDiscountPrice > 0 ? window.formatMoney(current_price) : window.formatMoney( item.shopifyFrontprice, window?.pageInfo?.currency?.money_format )}
                        </span>
                        <span class="text-[0.75rem] lg:text-[0.875rem] font-normal line-through pl-1">
                            ${ this.openAutoDiscountPrice && item.autoDiscountPrice > 0
                                ? origin_price > current_price ? window.formatMoney(origin_price) : "" : item.shopifyFrontOriginalPrice &&
                                item.shopifyFrontOriginalPrice > item.shopifyFrontprice ? `${window.formatMoney( item.shopifyFrontOriginalPrice, window.pageInfo.currency.money_format )}` : ""
                            }
                        </span>
                    </div>
                    <div class="shop-now">
                        <a href="${goodUrl}?variant=${skuId}" class="text-primary text-sm lg:text-xl font-semibold  flex items-center">
                            ${ this.text?.shop_now || 'Shop Now'} &nbsp;<jds-icon name="arrow-right-long"></jds-icon>
                        </a>
                    </div>
                </div>
            `
        })
        return html
    }

    /**
     * 渲染“只看不同”切换按钮
     * @returns {string} HTML 字符串
     */
    renderToggleBtn(){
        return `
            <div class="col-span-2 lg:col-span-4 flex justify中心 items-center bg-white toggle-diff">
                <div class="flex items-center cursor-pointer toggle-diff-wrapper">
                    <div class="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" class="sr-only peer toggle-diff-checkbox" ${
                        this._showDiff ? "checked" : ""
                        }>
                        <div class="w-11 h-6 bg-black/30 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FD5000]"></div>
                    </div>
                    <span class="ml-3 lg:ml-4 text-sm lg:text-xl font-semibold text-[#242F20] select-none">${
                        this.text.show_diff || 'Show only the difference'
                    }</span>
                </div>
            </div>
        `
    }
    /**
     * 渲染对比详情手风琴
     * @param {Array} list 详情数据
     * @returns {string} HTML 字符串
     */
    renderCompareDetails(list = []) {
        if (!Array.isArray(list) || list.length === 0) return "";
        return list.map((l1, index) => {
            const hasChildren = Array.isArray(l1.childInfos) && l1.childInfos.length > 0;
            const hasValues = Array.isArray(l1.showValue) && l1.showValue.length > 0;
            const contentHtml = hasChildren ? this.renderLevel2(l1.childInfos) : hasValues ? this.renderRowPC(l1, true, false)
                : "";
            return `
                <div class="compare-section last:border-b last:border-solid last:border-[#E2E3E3] data-index="${index}">
                    <div class="compare-header bg-[#E8E8E5] h-10 lg:h-14 pl-4 lg:pl-5 pr-3 flex items-center justify-between cursor-pointer select-none group active border-b border-solid border-[#E2E3E3]">
                        <span class="font-semibold text-base lg:text-2xl text-[#242F20]/[0.9]">${l1.infoShowTitle}</span>
                        <span class="transition-transform duration-300 transform group-[.active]:rotate-180 flex items-center justify-center">
                            <svg class="w-4 h-4 lg:w-6 lg:h-6" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 9L12 15L18 9" stroke="#00000099" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                    </div>
                    <div class="compare-content overflow-hidden transition-all duration-300 border-t border-[#E2E3E3]">
                        ${contentHtml}
                    </div>
                </div>
            `
        }).join("");
    }
    /**
     * 渲染二级对比行。
     * @param {Array} l2List 二级项目列表
     * @returns {string} HTML 字符串
     */
    renderLevel2(l2List) {
      if (!l2List || l2List.length === 0) return "";

      return l2List
        .map((l2) => {
          const isGroup = l2.childInfos && l2.childInfos.length > 0;
           const hasValues = Array.isArray(l2.showValue) && l2.showValue.length > 0;
            if (isGroup) {
              const rows = l2.childInfos.map((l3) => this.renderRowPC(l3)).join("");
              return `
                 <div class="w-[21.5rem] lg:w-full mx-auto lg:flex border-b lg:border-solid border-[#E2E3E3] last:border-0">
                     <div class="w-full lg:w-[7.8125rem] shrink-0 lg:p-6 py-4 lg:flex items-center justify-center font-bold text-[#242F20] text-base bg-white border-b lg:border-b-0 lg:text-center lg:border-r border-solid border-[#E2E3E3]">
                         ${l2.infoShowTitle}
                     </div>
                     <div class="flex-1">
                         ${hasValues ? this.renderRowPC(l2, true, false) : rows }
                     </div>
                 </div>
              `;
            } else {
              return this.renderRowPC(l2, true);
            }
        })
        .join("");
    }

    /**
     * 渲染 PC/移动端的单行对比值。
     * @param {Object} node 数据节点
     * @param {boolean} isFullWidth 是否全宽行
     * @param {boolean} showTitle 是否在单元格中显示标题（用于移动端布局）
     * @returns {string} HTML 字符串
     */
    renderRowPC(node, isFullWidth = false, showTitle = true) {
      const values = node.showValue || [];
      const gridClass = "w-[21.5rem] lg:w-full mx-auto grid gap-4 lg:gap-6 grid-cols-[10.25rem_10.25rem] lg:grid-cols-[16.25rem_16.25rem_16.25rem]";
      const paddingClass = isFullWidth ? "lg:pl-[7.8125rem]" : "";
      const cells = values.map(val => `
            <div class="py-3 pr-4 lg:p-6 min-h-[3.25rem] lg:min-h-[5rem] flex flex-col ">
                ${showTitle ? `<span class="text-[0.75rem] lg:text-[1rem] leading-[1.2] text-[#818181] mb-2 break-words font-normal">${node.infoShowTitle}</span>` : ''}
                <span class="text-[0.75rem] lg:text-[1rem] leading-[1.2] text-[#242F20] whitespace-pre-line break-words font-normal">${val || "一"}</span>
            </div>
        `
        )
        .join("");
      return `
            <div class="${gridClass} lg:border-b border-solid border-[#E2E3E3] last:border-0 ${paddingClass} bg-white">
                ${cells}
            </div>
        `;
    }
  }
  customElements.define("v5-app-compare-modal", V5AppCompareModal);
}
